import {faPencilAlt, faTimes} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Mutation} from 'react-apollo';
import {Button, Col, Container, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import {PulseLoader} from 'react-spinners';
import Error from '../../../common/components/Error/Error';
import PopIn from '../../../common/components/PopIn';
import {
  CancelButton, DeleteLogoButton,
  Div,
  FileDiv,
  FileInput,
  FileLabel, FileNameDiv,
  FooterRow,
  GradientCol,
  Input,
  TitleButton,
} from '../../../common/components/StyledComp';
import Tooltip from '../../../common/components/Tooltip';
import {IMAGE_MIME_TYPES} from '../../../common/constants';
import withUpload from '../../../common/HOC/withUpload';
import {GRAPHQL_MUTATION_UPDATE_BRAND} from '../../../common/queries/brand';

const UpdateBrand = ({brand: {id, name, file, extraFile}, t}) => {

  const updateFile = null;
  const updateExtraFile = null;
  const [modalVisible, setModalVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newBrand, setNewBrand] = useState({name, updateFile, updateExtraFile});
  const [errorUpload, setErrorUpload] = useState(null);
  const [wrongFormat, setWrongFormat] = useState(false);
  const [wrongExtraFormat, setWrongExtraFormat] = useState(false);
  const [fileName, setFileName] = useState(file ? file.filename : '');
  const [extraFileName, setExtraFileName] = useState(extraFile ? extraFile.filename : '');
  const [deleteExtraFile, setDeleteExtraFile] = useState(false);

  const resetUpdate = () => {
    setModalVisible(false);
    setSubmitted(false);
    setErrorUpload(null);
    setWrongFormat(false);
    setWrongExtraFormat(false);
    setDeleteExtraFile(false);
    setNewBrand({name, updateFile, updateExtraFile});
  };

  const onFileUploaded = async event => {
    const files = event.target.files;
    try {
      if (IMAGE_MIME_TYPES.includes(files[0].type)) {
        setWrongFormat(false);
        setFileName(files[0].name);
        setNewBrand({...newBrand, updateFile: files[0]});
      } else {
        const ext = files[0].type.split('/')[1].toString();
        setWrongFormat(ext);
      }
    } catch (e) {
      setErrorUpload(e);
    }
  };

  const onExtraFileUpload = async event => {
    const files = event.target.files;
    try {
      if (IMAGE_MIME_TYPES.includes(files[0].type)) {
        setWrongExtraFormat(false);
        setExtraFileName(files[0].name);
        setNewBrand({...newBrand, updateExtraFile: files[0]});
      } else {
        const ext = files[0].type.split('/')[1].toString();
        setWrongExtraFormat(ext);
      }
    } catch (e) {
      setErrorUpload(e);
    }
  };

  return (
    <Mutation
      mutation={GRAPHQL_MUTATION_UPDATE_BRAND}
      onCompleted={resetUpdate}
      onError={error => {
        console.error(error);
        setSubmitted(false);
      }}
      refetchQueries={['brands']}
    >
      {(updateBrand, {loading, error}) => {
        if (loading) {
          return <PulseLoader loading={loading} />;
        } else
          return (
            <>
              <Button className="btn-update" onClick={() => setModalVisible(true)}>
                <FontAwesomeIcon icon={faPencilAlt} color="#329aef" size="sm" />
              </Button>

              <PopIn visible={modalVisible} setModalVisible={setModalVisible} title={t('update')}>
                <Container>
                  <Row>
                    <Col xs={9} className="extended-col">
                      <Div className="attribute-name">{`${t('name')} : `}</Div>
                      <PulseLoader loading={loading} />
                      <Input
                        type="text"
                        value={newBrand.name}
                        name="brand_name"
                        onChange={e => {
                          setSubmitted(false);
                          setNewBrand({...newBrand, name: e.target.value});
                        }}
                        required
                      />
                      <Tooltip show={newBrand.name === '' && submitted} color="red">
                        {t('addBrand.requiredField')}
                      </Tooltip>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={9}>
                      <Div className="attribute-name">{`${t('logo')} : `}</Div>
                      <FileLabel>
                        {' '}
                        <FileNameDiv>{fileName}</FileNameDiv>
                        <FileDiv>{t('browse')}</FileDiv>
                        <FileInput type="file" accept="image/*" onChange={onFileUploaded} required />
                      </FileLabel>
                      <Tooltip show={wrongFormat} color="red">
                        {t('addBrand.format') + wrongFormat}
                      </Tooltip>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={9}>
                      <Div className="attribute-name">{`${t('optionalLogo')} : `}</Div>
                      <FileLabel>
                        {' '}
                        <FileNameDiv>{extraFileName}</FileNameDiv>
                        {extraFileName && (
                          <DeleteLogoButton onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            setDeleteExtraFile(true);
                            setExtraFileName('');
                            setNewBrand({...newBrand, updateExtraFile: null});
                          }}>
                            <FontAwesomeIcon icon={faTimes} color="red" size="sm" />
                          </DeleteLogoButton>
                        )}
                        <FileDiv>
                          {t('browse')}
                        </FileDiv>
                        <FileInput type="file"  accept="image/*" onChange={onExtraFileUpload}/>
                      </FileLabel>
                      <Tooltip show={wrongExtraFormat} color="red">
                        {t('addBrand.format') + wrongExtraFormat}
                      </Tooltip>
                    </Col>
                  </Row>
                  <FooterRow>
                    <GradientCol sm={{span: 6, offset: 3}}>
                      <Error error={errorUpload} />
                      <Error error={error} />
                      <div className="popin-buttons">
                        <TitleButton
                          disabled={loading}
                          onClick={() => {
                            setSubmitted(true);
                            if (newBrand.name !== '' && !wrongFormat) {
                              const input = {id, name: newBrand.name};
                              if (newBrand.updateFile !== '') {
                                input.updateFile = newBrand.updateFile;
                              }
                              if (newBrand.updateExtraFile !== '') {
                                input.updateExtraFile = newBrand.updateExtraFile;
                              }
                              if (deleteExtraFile) {
                                input.deleteExtraFile = deleteExtraFile;
                              }
                              updateBrand({variables: {input}});
                            }
                          }}>
                          {t('save')}
                        </TitleButton>
                        <CancelButton disabled={submitted} onClick={resetUpdate}
                          style={{marginTop: 0, width: 'auto'}}>
                          {t('cancel')}
                        </CancelButton>
                      </div>
                    </GradientCol>
                  </FooterRow>
                </Container>
              </PopIn>
            </>
          );
      }}
    </Mutation>
  );
};

UpdateBrand.propTypes = {
  brand: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    file: PropTypes.shape({
      filename: PropTypes.string,
    }),
    extraFile: PropTypes.shape({
      filename: PropTypes.string,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
};

export default compose(
  withUpload,
  withTranslation('brand'),
)(UpdateBrand);
